/**
 * Evolution transition (the showpiece): the old creature floats in a DNA vortex, dissolves
 * into glowing particles that spiral up a double helix and re-form as the new creature.
 * Driven by `progress` phases set from the UI timeline:
 *   0..1  morph (particles old → swirl → new), with the models cross-faded at the ends.
 */
import {
  AdditiveBlending,
  InstancedBufferAttribute,
  Sprite,
  AmbientLight,
  Color,
  DirectionalLight,
  FogExp2,
  Group,
  Object3D,
  PerspectiveCamera,
  PointLight,
  Scene,
  Vector3,
  Mesh,
  type Material,
} from 'three';
import type { StageId } from '@core/tree';
import { TREE } from '@core/tree';
import { disposeTree, loadPreview, makeHelix, makeMotes, makePortal, samplePoints, uni, type PreviewDeps, type UniformMaterial } from './common';
import { PointsNodeMaterial } from 'three/webgpu';
import { clamp, cos, float, instancedBufferAttribute, length, mix, sin, smoothstep, uniform, uv, varying, vec3, vec4 } from 'three/tsl';

/**
 * Particle morph (TSL): each instanced sprite travels from a point on the old creature (`aStart`)
 * through a swirling vortex to a point on the new one (`aEnd`), staggered by `aRand.x`.
 */
function makeMorph(start: Float32Array, end: Float32Array, rand: Float32Array, from: Color, to: Color): Sprite {
  const count = start.length / 3;
  const uT = uniform(0);
  const uTime = uniform(0);
  const uOpacity = uniform(0);
  const aStart = instancedBufferAttribute(new InstancedBufferAttribute(start, 3), 'vec3') as unknown as ReturnType<typeof vec3>;
  const aEnd = instancedBufferAttribute(new InstancedBufferAttribute(end, 3), 'vec3') as unknown as ReturnType<typeof vec3>;
  const aRand = instancedBufferAttribute(new InstancedBufferAttribute(rand, 4), 'vec4') as unknown as ReturnType<typeof vec4>;
  const t = clamp(uT.sub(aRand.x.mul(0.25)).div(0.75), 0, 1);
  const e = t.mul(t).mul(float(3).sub(t.mul(2)));
  const swirl = sin(t.mul(Math.PI));
  const ang = t.mul(Math.PI * 2).mul(aRand.y.add(1.5)).add(aRand.z.mul(Math.PI * 2)).add(uTime.mul(0.6).mul(swirl));
  const r = swirl.mul(aRand.w.mul(1.4).add(0.9));
  const base = mix(aStart, aEnd, e);
  const mat = new PointsNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending, sizeAttenuation: true });
  mat.positionNode = vec3(
    base.x.add(cos(ang).mul(r)),
    base.y.add(swirl.mul(aRand.y.sub(0.5)).mul(2.4)).add(sin(uTime.mul(3).add(aRand.z.mul(20))).mul(0.02)),
    base.z.add(sin(ang).mul(r)),
  );
  mat.sizeNode = float(0.075).mul(aRand.w.mul(0.8).add(0.6)).mul(swirl.mul(0.8).add(1));
  const mixV = varying(e);
  const aV = varying(swirl.mul(0.45).add(0.55));
  const d = smoothstep(0.5, 0.0, length(uv().sub(0.5)));
  mat.colorNode = mix(uniform(from), uniform(to), mixV).mul(d.mul(1.3).add(0.7));
  mat.opacityNode = d.mul(d).mul(uOpacity).mul(aV);
  (mat as unknown as UniformMaterial).uniforms = { uT, uTime, uOpacity };
  const sprite = new Sprite(mat);
  sprite.count = count;
  sprite.frustumCulled = false;
  return sprite;
}

export class TransitionScene {
  readonly scene = new Scene();
  readonly camera = new PerspectiveCamera(42, 1, 0.1, 100);
  private stageGroup = new Group();
  private helix: Group;
  private portal: ReturnType<typeof makePortal>;
  private motes: ReturnType<typeof makeMotes>;
  private fromObj?: Object3D;
  private toObj?: Object3D;
  private points?: Sprite;
  private light: PointLight;
  private t = 0;
  private count: number;
  /** Morph progress 0..1 (set by the UI timeline). */
  progress = 0;
  /** Helix/vortex energy 0..1. */
  energy = 0;
  /** Final reveal 0..1 (new creature scale-in + camera settle). */
  reveal = 0;

  constructor(private readonly deps: PreviewDeps, private readonly from: StageId, private readonly to: StageId) {
    this.count = deps.quality.tier === 'low' ? 1800 : deps.quality.tier === 'medium' ? 3500 : 6000;
    this.scene.background = new Color(0x010604);
    this.scene.fog = new FogExp2(0x010604, 0.05);
    this.scene.add(new AmbientLight(0xcfffe6, 1.0));
    const key = new DirectionalLight(0xffffff, 2);
    key.position.set(2, 3, 4);
    this.scene.add(key);
    this.light = new PointLight(0x97ff5a, 20, 12, 1.4);
    this.light.position.set(0, 1, 2);
    this.scene.add(this.light);

    this.portal = makePortal(5, 0x97ff5a, 0x1f8a6a);
    this.portal.position.set(0, 0, -5);
    this.scene.add(this.portal);
    this.helix = makeHelix({ turns: 4, height: 8, radius: 1.7, dot: 0.05, rungs: false, perTurn: deps.quality.tier === 'low' ? 16 : 28 });
    this.scene.add(this.helix);
    this.motes = makeMotes(deps.quality.tier === 'low' ? 100 : 260, new Vector3(16, 20, 12), 0xc9ff8a, 0.6);
    this.scene.add(this.motes);
    this.stageGroup.position.y = 0.32;
    this.helix.position.set(0, 0.32, -1.9);
    this.scene.add(this.stageGroup);
    this.camera.position.set(0, 0.2, 4.2);
    this.camera.lookAt(0, 0, 0);
  }

  /** Load both previews and build the particle morph. */
  async load(): Promise<void> {
    const [a, b] = await Promise.all([loadPreview(this.from, this.deps, 1.3), loadPreview(this.to, this.deps, 1.25)]);
    this.fromObj = a;
    this.toObj = b;
    b.visible = false;
    this.stageGroup.add(a, b);
    const start = samplePoints(a, this.count, 0.6);
    const end = samplePoints(b, this.count, 0.7);
    const rand = new Float32Array(this.count * 4);
    for (let i = 0; i < rand.length; i++) rand[i] = Math.random();
    this.points = makeMorph(start, end, rand, new Color(TREE[this.from].color), new Color(TREE[this.to].color));
    this.stageGroup.add(this.points);
  }

  private setOpacity(root: Object3D | undefined, o: number): void {
    if (!root) return;
    root.visible = o > 0.01;
    root.traverse((x) => {
      const m = x as Mesh;
      if (!m.isMesh) return;
      const mats = (Array.isArray(m.material) ? m.material : [m.material]) as Material[];
      for (const mat of mats) {
        const ud = mat.userData as { uiBase?: { t: boolean; o: number } };
        if (!ud.uiBase) ud.uiBase = { t: mat.transparent, o: mat.opacity };
        mat.transparent = o < 0.999 || ud.uiBase.t;
        mat.opacity = ud.uiBase.o * o;
      }
    });
  }

  update(dt: number): void {
    this.t += dt;
    const t = this.t;
    const p = this.progress;
    const e = this.energy;
    this.helix.rotation.y = t * (0.6 + e * 2.4);
    this.helix.scale.setScalar(0.9 + e * 0.12);
    this.helix.visible = e > 0.02;
    this.helix.children.forEach((c) => {
      const m = (c as Mesh).material as Material;
      m.transparent = true;
      m.opacity = Math.min(1, e * 1.4) * (c === this.helix.children[2] ? 0.35 : 1);
    });
    const pu = uni(this.portal);
    pu.uTime.value = t * (1 + e * 2);
    pu.uIntensity.value = 0.3 + e * 0.7;
    uni(this.motes).uTime.value = t * (1 + e * 3);

    // Old creature: visible until the dissolve starts; new creature fades in at the end.
    const fromO = 1 - smooth(0.0, 0.12, p);
    const toO = smooth(0.86, 1.0, p);
    this.setOpacity(this.fromObj, fromO);
    this.setOpacity(this.toObj, toO);
    if (this.fromObj) {
      this.fromObj.rotation.y = t * 0.8;
      this.fromObj.scale.setScalar(1 + p * 0.3);
    }
    if (this.toObj) {
      const r = this.reveal;
      this.toObj.rotation.y = t * 0.5 + (1 - r) * 3;
      this.toObj.scale.setScalar(0.85 + toO * 0.15 + Math.sin(r * Math.PI) * 0.08);
    }
    if (this.points) {
      const u = uni(this.points);
      u.uT.value = p;
      u.uTime.value = t;
      u.uOpacity.value = smooth(0.0, 0.08, p) * (1 - smooth(0.9, 1.0, p) * 0.9);
      this.points.rotation.y = this.fromObj ? this.fromObj.rotation.y * (1 - p) + (this.toObj?.rotation.y ?? 0) * p : 0;
    }
    this.light.intensity = 12 + e * 40;
    // Camera: slow push-in + orbit during the swirl, settles for the reveal.
    const orbit = Math.sin(t * 0.35) * 0.22 + e * Math.sin(t * 0.9) * 0.18;
    const dist = 4.9 - e * 0.5 - this.reveal * 0.4;
    this.camera.position.set(Math.sin(orbit) * dist, 0.25 + e * 0.3, Math.cos(orbit) * dist);
    this.camera.lookAt(0, 0.1, 0);
  }

  dispose(): void {
    for (const o of [this.fromObj, this.toObj]) if (o) this.setOpacity(o, 1);
    disposeTree(this.scene);
  }
}

function smooth(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}
